import { useEffect, useMemo, useState } from 'react';
import { FIRESTORE_COLLECTIONS } from '../firebase';
import { useI18n } from '../i18n';
import type { TonyteActivity } from '../types';
import { getSafeTags } from '../utils/activityTags';
import { getTranslatedText } from '../utils/translations';

type TranslatedActivityText = {
  title: string;
  description: string;
  tags: string[];
  translatingTitle: boolean;
  translatingDescription: boolean;
  translatingTags: boolean;
};

const EMPTY_ACTIVITY: TonyteActivity = {
  id: '',
  title: '',
  description: '',
  location: '',
  address: '',
  city: '',
  category: 'social',
  entryType: 'official_event',
  sourceType: 'user_activity',
  hostId: '',
  hostName: '',
  startDate: '',
  startTime: '',
  coordinates: null,
  approved: true,
  isFree: true,
  maxParticipants: 0,
  createdAt: '',
};

const getActivityCollection = (activity: TonyteActivity) =>
  activity.sourceCollection ||
  (activity.entryType === 'community_plan' ? FIRESTORE_COLLECTIONS.plans : FIRESTORE_COLLECTIONS.activities);

function getAllActivityTags(activity: TonyteActivity): string[] {
  return getSafeTags([
    ...(activity.tags || []),
    ...(activity.musicGenres || []),
    ...(activity.hashtags || []),
  ]);
}

export function useTranslatedActivity(activityInput: TonyteActivity | null | undefined): TranslatedActivityText {
  const activity = activityInput || EMPTY_ACTIVITY;
  const { language } = useI18n();
  const cachedTitle = language === 'en' ? '' : activity.translations?.[language]?.title || '';
  const cachedDescription = language === 'en' ? '' : activity.translations?.[language]?.description || '';
  const rawTags = useMemo(
    () => getAllActivityTags(activity),
    // Deliberately field-level, not [activity]: callers commonly pass a
    // freshly-constructed activity object per render (e.g. list .map()),
    // and depending on the whole object would recompute (and re-trigger the
    // translation fetch effects below) on every render even when the
    // underlying tag data hasn't changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activity.tags, activity.musicGenres, activity.hashtags],
  );
  const cachedTagsStr = language === 'en' ? '' : (activity.translations?.[language] as any)?.tags || '';
  const [title, setTitle] = useState(cachedTitle || activity.title);
  const [description, setDescription] = useState(cachedDescription || activity.description || '');
  const [tags, setTags] = useState<string[]>(
    cachedTagsStr ? getSafeTags(cachedTagsStr.split('|||')) : rawTags,
  );
  const [translatingTitle, setTranslatingTitle] = useState(false);
  const [translatingDescription, setTranslatingDescription] = useState(false);
  const [translatingTags, setTranslatingTags] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const originalTitle = activity.title || '';
    const nextCachedTitle = language === 'en' ? '' : activity.translations?.[language]?.title || '';
    setTitle(nextCachedTitle || originalTitle);
    if (language === 'en' || nextCachedTitle || !originalTitle.trim()) {
      setTranslatingTitle(false);
      return () => { cancelled = true; };
    }

    if (!activity.id) {
      setTranslatingTitle(false);
      return () => { cancelled = true; };
    }
    setTranslatingTitle(true);
    getTranslatedText({
      collectionName: getActivityCollection(activity),
      docId: activity.id,
      field: 'title',
      originalText: originalTitle,
      targetLanguage: language,
      sourceLanguage: 'en',
      context: activity.entryType === 'community_plan'
        ? 'community plan title in a social events app'
        : 'event title in a social events app',
    })
      .then(text => { if (!cancelled) setTitle(text); })
      .finally(() => { if (!cancelled) setTranslatingTitle(false); });
    return () => { cancelled = true; };
    // Field-level deps only (not [activity]) — see rawTags comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity.id, activity.title, activity.sourceCollection, activity.entryType, cachedTitle, language]);

  useEffect(() => {
    let cancelled = false;
    const originalDescription = activity.description || '';
    const nextCachedDescription = language === 'en' ? '' : activity.translations?.[language]?.description || '';
    setDescription(nextCachedDescription || originalDescription);
    if (language === 'en' || nextCachedDescription || !originalDescription.trim()) {
      setTranslatingDescription(false);
      return () => { cancelled = true; };
    }

    if (!activity.id) {
      setTranslatingDescription(false);
      return () => { cancelled = true; };
    }
    setTranslatingDescription(true);
    getTranslatedText({
      collectionName: getActivityCollection(activity),
      docId: activity.id,
      field: 'description',
      originalText: originalDescription,
      targetLanguage: language,
      sourceLanguage: 'en',
      context: activity.entryType === 'community_plan'
        ? 'community plan description in a social events app'
        : 'event description in a social events app',
    })
      .then(text => { if (!cancelled) setDescription(text); })
      .finally(() => { if (!cancelled) setTranslatingDescription(false); });
    return () => { cancelled = true; };
    // Field-level deps only (not [activity]) — see rawTags comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity.id, activity.description, activity.sourceCollection, activity.entryType, cachedDescription, language]);

  useEffect(() => {
    let cancelled = false;
    const nextCachedTagsStr = language === 'en' ? '' : (activity.translations?.[language] as any)?.tags || '';
    if (language === 'en' || nextCachedTagsStr) {
      setTags(nextCachedTagsStr
        ? getSafeTags(nextCachedTagsStr.split('|||'))
        : rawTags,
      );
      setTranslatingTags(false);
      return () => { cancelled = true; };
    }
    if (!rawTags.length) {
      setTags([]);
      setTranslatingTags(false);
      return () => { cancelled = true; };
    }
    setTranslatingTags(true);
    const joinedTags = rawTags.join('|||');
    getTranslatedText({
      collectionName: getActivityCollection(activity),
      docId: activity.id,
      field: 'tags',
      originalText: joinedTags,
      targetLanguage: language,
      sourceLanguage: 'en',
      context: 'list of event category tags separated by |||. Translate each tag but keep the ||| separators exactly.',
    })
      .then(text => {
        if (!cancelled) {
          setTags(getSafeTags(text.split('|||')));
        }
      })
      .finally(() => { if (!cancelled) setTranslatingTags(false); });
    return () => { cancelled = true; };
    // Field-level deps only (not [activity]) — see rawTags comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity.id, activity.sourceCollection, activity.entryType, rawTags, language]);

  return useMemo(() => ({
    title,
    description,
    tags: getSafeTags(tags),
    translatingTitle,
    translatingDescription,
    translatingTags,
  }), [description, tags, title, translatingDescription, translatingTags, translatingTitle]);
}
